// Edit a value where it is written, instead of opening a form to change one word.
//
// ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────────
// Renaming a task was: click the chip, wait for the panel, find the title field, type, press Opslaan,
// wait, close the panel. Six actions and a round trip for a typo. In every tool this app is measured
// against you double-click the text and type. The panel is still there for everything else — this is
// only for the one field a planner changes most often, which is the one that should cost the least.
//
// ── WHAT IT REFUSES TO DO ───────────────────────────────────────────────────────
// It does not save on every keystroke. A planner mid-word is not making a decision, and a board that
// writes on each letter turns one rename into fifteen audit entries against a rate-limited endpoint.
// It commits on Enter or on blur, and only when the value actually changed.
//
// It also does not swallow Escape: abandoning an edit must restore what was there, because the most
// common reason to press Escape is having typed into the wrong thing.
import { useEffect, useRef, useState } from 'react';

export function InlineEdit({
  value, onCommit, className, placeholder, ariaLabel, multiline,
}: {
  value: string;
  /** Called only when the text actually changed. Rejecting the promise puts the old text back. */
  onCommit: (next: string) => Promise<void> | void;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  // A REFETCH MUST NOT OVERWRITE WHAT SOMEBODY IS TYPING. The draft follows the prop only while the
  // field is closed; while it is open the person at the keyboard owns it.
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  useEffect(() => {
    if (!editing) return;
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const commit = async () => {
    const next = draft.trim();
    setEditing(false);
    if (next === value.trim() || next === '') { setDraft(value); return; }
    setBusy(true);
    try {
      await onCommit(next);
    } catch {
      // The caller reports the refusal; this only has to stop showing text the server rejected.
      setDraft(value);
    } finally {
      setBusy(false);
    }
  };

  if (!editing) {
    return (
      <span className={`inline-edit ${className ?? ''}${busy ? ' busy' : ''}`}
            // DOUBLE-CLICK, NOT CLICK. A single click on a chip selects it, and a board where every
            // stray click opens a text cursor is a board you cannot select anything in.
            onDoubleClick={(e) => { e.stopPropagation(); setEditing(true); }}
            title="Dubbelklik om te hernoemen">
        {value || <span className="faint">{placeholder}</span>}
      </span>
    );
  }

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const onKey = (e: React.KeyboardEvent) => {
    // KEYSTROKES STAY IN THE FIELD. Without this, typing "d" while renaming switches the calendar to
    // the day view and "+" lengthens the job behind the input.
    e.stopPropagation();
    if (e.key === 'Escape') { setDraft(value); setEditing(false); }
    if (e.key === 'Enter' && (!multiline || e.metaKey || e.ctrlKey)) { e.preventDefault(); void commit(); }
  };

  return multiline ? (
    <textarea ref={ref} className={`inline-input ${className ?? ''}`} rows={2} value={draft}
              aria-label={ariaLabel} placeholder={placeholder}
              onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => void commit()}
              onClick={stop} onPointerDown={stop} onDoubleClick={stop} />
  ) : (
    <input ref={ref} className={`inline-input ${className ?? ''}`} value={draft}
           aria-label={ariaLabel} placeholder={placeholder}
           onChange={(e) => setDraft(e.target.value)} onKeyDown={onKey} onBlur={() => void commit()}
           onClick={stop} onPointerDown={stop} onDoubleClick={stop} />
  );
}
